import React from 'react';
import { useQuery } from '@tanstack/react-query';
import {
  Select,
  Text,
  TextField,
  LoadingOrbit,
  Checkbox,
  NumberField,
  TooltipTrigger,
  Tooltip,
  DocsAnnotation,
  TagBadge,
} from '@chatsdet/ui';
import { Info } from '@phosphor-icons/react';
import { API_URL } from '../../../config';
import { toApiError } from '../../../lib/api-error';
import { normalizeConcreteWorkflowId } from './workflowIds';
import { useResolvedAttachments } from '../../../features/attachments/api';
import { getFileTypeBadge } from '../../../features/attachments/utils';

type SeedNodeConfigProps = {
  isActive: boolean;
  localData: Record<string, any>;
  setLocalData: React.Dispatch<React.SetStateAction<Record<string, any>>>;
  workflowId?: string;
  onImmediateUpdate?: (data: Record<string, any>) => void;
};

interface SeedFlow {
  id: string;
  name: string;
  snapshotVersion: number;
  publishedAt: string | null;
  teardownWorkflowId: string | null;
  seedRunOncePerSuite?: boolean;
}

interface Snapshot {
  version: number;
  publishedAt: string;
  publishedBy: string | null;
}

interface SeedValueInput {
  kind: 'value';
  name: string;
  label: string;
  required?: boolean;
  description?: string;
  placeholder?: string;
  defaultValue?: string;
}

export const SeedNodeConfig: React.FC<SeedNodeConfigProps> = ({
  isActive,
  localData,
  setLocalData,
  workflowId,
  onImmediateUpdate,
}) => {
  const seedWorkflowId = normalizeConcreteWorkflowId(localData.seedWorkflowId);
  const hasConcreteSeedWorkflow = seedWorkflowId !== null;

  // Fetch available seed workflows for the org (excluding current workflow)
  const seedFlowsQ = useQuery({
    queryKey: ['seed-flows', workflowId],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.set('flowType', 'seed');
      if (workflowId) params.set('excludeWorkflowId', workflowId);
      const r = await fetch(`${API_URL}/workflows/setup?${params.toString()}`, {
        credentials: 'include',
      });
      if (!r.ok) throw new Error('Failed to fetch seed flows');
      const data = await r.json();
      return (data.workflows || []) as SeedFlow[];
    },
    enabled: isActive,
  });

  // Fetch available snapshots for the selected seed workflow
  const snapshotsQ = useQuery({
    queryKey: ['node-config-snapshots', seedWorkflowId],
    queryFn: async () => {
      if (!seedWorkflowId) return [];

      const r = await fetch(`${API_URL}/workflows/${seedWorkflowId}/snapshots`, {
        credentials: 'include',
      });
      if (r.status === 404) return [];
      if (!r.ok) throw await toApiError(r, 'Failed to fetch snapshots');
      const data = await r.json();
      return (data.snapshots || []) as Snapshot[];
    },
    enabled: isActive && hasConcreteSeedWorkflow,
  });

  // Fetch the selected seed flow's declared value inputs (for per-node overrides)
  const seedInputsQ = useQuery({
    queryKey: ['node-config-seed-inputs', seedWorkflowId],
    queryFn: async () => {
      if (!seedWorkflowId) return [];

      const r = await fetch(`${API_URL}/workflows/${seedWorkflowId}/seed-outputs`, {
        credentials: 'include',
      });
      if (r.status === 404) return [];
      if (!r.ok) throw await toApiError(r, 'Failed to fetch seed inputs');
      const data = await r.json();
      return (data.inputs || []) as SeedValueInput[];
    },
    enabled: isActive && hasConcreteSeedWorkflow,
  });

  const flowOptions = (seedFlowsQ.data || []).map((flow) => ({
    id: flow.id,
    label: flow.name,
  }));

  // Seed flows can force suite scope; the node-level control is then locked.
  const forcedSuite =
    seedFlowsQ.data?.find((f) => f.id === seedWorkflowId)?.seedRunOncePerSuite ?? false;
  // Effective lifecycle scope: 'inline' (beforeEach), 'suite' (beforeAll, whole
  // suite), or 'layer' (bracket the consuming layer). Forced-suite wins over 'layer'.
  const effectiveScope: 'inline' | 'suite' | 'layer' = forcedSuite
    ? 'suite'
    : ((localData.scope as 'inline' | 'suite' | 'layer' | undefined) ?? 'inline');
  // Suite scope locks per-node input overrides; layer/inline keep them.
  const effectiveSuiteScope = hasConcreteSeedWorkflow && effectiveScope === 'suite';
  const seedValueInputs = seedInputsQ.data ?? [];

  // Seed attachments are spec-authored only (`- attach: <filename>` under a
  // `**Seed**` journey line — see packages/canary-spec/src/emit.ts). The
  // write-time chokepoint (`resolveNodeConfigForWrite` in
  // packages/db/src/workflow-node-name-resolution.ts) resolves
  // `attachmentRef` to `attachmentIds` once, and never re-resolves once
  // `attachmentIds` is populated — so this stays read-only rather than a
  // full `AttachmentPickerDialog`. Letting a user pick a *different*
  // attachment here would silently diverge from the spec's `attach:` bullet
  // with no sync path back: the next `spec compile` wouldn't overwrite it
  // (resolution only fires when `attachmentIds` is empty), so the UI value
  // would win forever without the spec author ever knowing. Edit the spec to
  // change it.
  const attachmentIds = Array.isArray(localData.attachmentIds)
    ? (localData.attachmentIds as unknown[]).filter((id): id is string => typeof id === 'string')
    : [];
  const attachmentRef =
    typeof localData.attachmentRef === 'string' ? localData.attachmentRef.trim() : '';
  const resolvedAttachments = useResolvedAttachments(attachmentIds);
  const attachments = resolvedAttachments.data?.data ?? [];

  const snapshotOptions = [
    { id: 'latest', label: 'Live (latest definition)' },
    ...(snapshotsQ.data || []).map((snapshot) => ({
      id: String(snapshot.version),
      label: `v${snapshot.version} (${new Date(snapshot.publishedAt).toLocaleDateString()})`,
    })),
  ];

  return (
    <DocsAnnotation slug="reference/flows" title="Seed Data Nodes" className="min-h-full">
      <div className="space-y-4">
        <Text className="text-sm text-muted-foreground">
          Select a seed script to provision shared test data before this workflow runs.
        </Text>

        {seedFlowsQ.isLoading ? (
          <LoadingOrbit />
        ) : flowOptions.length === 0 ? (
          <div className="p-4 rounded-lg bg-muted/50 space-y-2">
            <Text size="sm" className="text-muted-foreground font-medium">
              No promoted seed scripts available.
            </Text>
            <Text size="sm" className="text-muted-foreground">
              Create and promote a seed script first, then it will appear here.
            </Text>
          </div>
        ) : (
          <>
            <Select
              label="Seed Script"
              placeholder="Select a seed script"
              items={flowOptions}
              selectedKey={seedWorkflowId}
              onSelectionChange={(key) => {
                const flow = seedFlowsQ.data?.find((f) => f.id === key);
                const newData = {
                  ...localData,
                  seedWorkflowId: key as string,
                  seedWorkflowName: flow?.name ?? null,
                  snapshotVersion: null,
                };
                setLocalData(newData);
                onImmediateUpdate?.(newData);
              }}
            />

            {hasConcreteSeedWorkflow && snapshotsQ.isLoading && <LoadingOrbit />}

            {hasConcreteSeedWorkflow && !snapshotsQ.isLoading && snapshotOptions.length > 0 && (
              <Select
                label="Version"
                placeholder="Select a version"
                items={snapshotOptions}
                selectedKey={
                  localData.snapshotVersion === null || localData.snapshotVersion === undefined
                    ? 'latest'
                    : String(localData.snapshotVersion)
                }
                onSelectionChange={(key) => {
                  setLocalData({
                    ...localData,
                    snapshotVersion: key === 'latest' ? null : Number(key),
                  });
                }}
              />
            )}

            {hasConcreteSeedWorkflow && (
              <div className="space-y-1">
                <Select
                  label="Execution Mode"
                  items={[
                    { id: 'recommended', label: 'Recommended (API when available)' },
                    { id: 'api', label: 'API Only' },
                    { id: 'browser', label: 'Browser' },
                  ]}
                  selectedKey={localData.executionMode ?? 'recommended'}
                  onSelectionChange={(key) => {
                    setLocalData({
                      ...localData,
                      executionMode: key as string,
                    });
                  }}
                />
                {(localData.executionMode ?? 'recommended') === 'recommended' && (
                  <Text size="xs" className="text-muted-foreground">
                    Automatically uses API execution if the seed has an API sequence, otherwise
                    falls back to browser.
                  </Text>
                )}
              </div>
            )}

            {hasConcreteSeedWorkflow &&
              (localData.executionMode ?? 'recommended') !== 'browser' && (
                <NumberField
                  label="API call timeout (seconds)"
                  placeholder="120"
                  minValue={1}
                  value={localData.apiTimeoutSeconds ?? null}
                  onChange={(value) => {
                    setLocalData({
                      ...localData,
                      apiTimeoutSeconds: value && value > 0 ? Math.floor(value) : undefined,
                    });
                  }}
                  description="Per-request timeout for this seed's API (and teardown) calls. Leave blank for the default (120s). Ignored in Browser mode."
                />
              )}

            {hasConcreteSeedWorkflow && (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Select
                    label="When to run"
                    className="flex-1"
                    isDisabled={forcedSuite}
                    items={[
                      { id: 'inline', label: 'Each test (before every test that uses it)' },
                      { id: 'suite', label: 'Once per suite (before all layers)' },
                      { id: 'layer', label: 'Once per layer (before its layer)' },
                    ]}
                    selectedKey={effectiveScope}
                    onSelectionChange={(key) => {
                      const scope = key as 'inline' | 'suite' | 'layer';
                      setLocalData({
                        ...localData,
                        scope,
                        // Suite-scoped seeds always run with the seed flow's input
                        // defaults — clear stale per-node overrides. Layer/inline keep them.
                        inputValues: scope === 'suite' ? undefined : localData.inputValues,
                      });
                    }}
                  />
                  <TooltipTrigger delay={100}>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground transition-colors mt-6"
                    >
                      <Info size={14} />
                    </button>
                    <Tooltip>
                      {forcedSuite
                        ? 'This seed always runs once per suite — set on the seed flow itself and cannot be changed here.'
                        : 'Each test = beforeEach (default). Once per suite = beforeAll, shared across all layers. Once per layer = provisioned before its layer and torn down right after that layer.'}
                    </Tooltip>
                  </TooltipTrigger>
                </div>
              </div>
            )}

            {effectiveSuiteScope && (
              <div className="flex items-start gap-2 p-2 rounded-md bg-muted/50">
                <Info size={12} className="text-muted-foreground mt-0.5 shrink-0" />
                <Text size="xs" className="text-muted-foreground">
                  {forcedSuite
                    ? 'This seed flow is configured to always run once per suite. Created data is shared across all workflows that include it; teardown runs once at suite end.'
                    : 'Runs once per suite. Created data is shared across all workflows that include this seed; teardown runs once at suite end.'}
                </Text>
              </div>
            )}

            {hasConcreteSeedWorkflow && effectiveScope === 'layer' && (
              <div className="flex items-start gap-2 p-2 rounded-md bg-muted/50">
                <Info size={12} className="text-muted-foreground mt-0.5 shrink-0" />
                <Text size="xs" className="text-muted-foreground">
                  Provisioned once before the suite layer that uses it and torn down right after
                  that layer completes. Re-provisions per layer when consumed across multiple
                  layers. Outside a suite, this runs like “each test.”
                </Text>
              </div>
            )}

            {hasConcreteSeedWorkflow && !effectiveSuiteScope && seedValueInputs.length > 0 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <Text size="sm" className="font-medium">
                    Inputs
                  </Text>
                  <TooltipTrigger delay={100}>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Info size={14} />
                    </button>
                    <Tooltip>
                      Set values for this seed script&apos;s workflow inputs. Leave a field blank to
                      use the seed script&apos;s default.
                    </Tooltip>
                  </TooltipTrigger>
                </div>
                {seedValueInputs.map((input) => (
                  <TextField
                    key={input.name}
                    label={`${input.label || input.name}${input.required ? ' *' : ''}`}
                    placeholder={input.placeholder ?? input.defaultValue ?? ''}
                    description={input.description}
                    value={localData.inputValues?.[input.name] ?? ''}
                    onChange={(value) => {
                      const next: Record<string, string> = { ...localData.inputValues };
                      if (value === '') {
                        delete next[input.name];
                      } else {
                        next[input.name] = value;
                      }
                      setLocalData({
                        ...localData,
                        inputValues: Object.keys(next).length > 0 ? next : undefined,
                      });
                    }}
                  />
                ))}
              </div>
            )}

            {hasConcreteSeedWorkflow && (
              <div className="flex items-center gap-2">
                <Checkbox
                  isSelected={localData.onFailure === 'warn'}
                  onChange={(checked) => {
                    setLocalData({
                      ...localData,
                      onFailure: checked ? 'warn' : 'block',
                    });
                  }}
                  label="Continue on failure"
                />
                <TooltipTrigger delay={100}>
                  <button
                    type="button"
                    className="text-muted-foreground hover:text-foreground transition-colors"
                  >
                    <Info size={14} />
                  </button>
                  <Tooltip>
                    If checked, the workflow will continue even if this seed script fails.
                    Otherwise, it will be skipped.
                  </Tooltip>
                </TooltipTrigger>
              </div>
            )}

            {hasConcreteSeedWorkflow &&
              seedFlowsQ.data?.find((f) => f.id === seedWorkflowId)?.teardownWorkflowId && (
                <div className="flex items-center gap-2">
                  <Checkbox
                    isSelected={localData.runTeardown !== false}
                    onChange={(checked) => {
                      setLocalData({
                        ...localData,
                        runTeardown: checked,
                      });
                    }}
                    label="Run teardown"
                  />
                  <TooltipTrigger delay={100}>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Info size={14} />
                    </button>
                    <Tooltip>Automatically clean up seed data after tests complete.</Tooltip>
                  </TooltipTrigger>
                </div>
              )}

            {(attachmentIds.length > 0 || attachmentRef) && (
              <div className="space-y-1">
                <div className="flex items-center gap-2">
                  <Text size="sm" className="font-medium">
                    Attachment
                  </Text>
                  <TooltipTrigger delay={100}>
                    <button
                      type="button"
                      className="text-muted-foreground hover:text-foreground transition-colors"
                    >
                      <Info size={14} />
                    </button>
                    <Tooltip>
                      Set via this seed script&apos;s spec (an <code>attach:</code> bullet). Edit
                      the spec to change it.
                    </Tooltip>
                  </TooltipTrigger>
                </div>
                {attachments.length > 0 ? (
                  attachments.map((attachment) => (
                    <div
                      key={attachment.id}
                      className="flex items-center gap-2 rounded-md bg-muted/30 px-2 py-1"
                    >
                      <TagBadge
                        name={getFileTypeBadge(attachment.contentType)}
                        color="gray"
                        hideDot
                        size="sm"
                      />
                      <div className="flex-1 min-w-0">
                        <Text size="sm" className="truncate">
                          {attachment.filename}
                        </Text>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="flex items-center gap-2 rounded-md bg-muted/30 px-2 py-1">
                    <div className="flex-1 min-w-0">
                      <Text size="sm" className="truncate text-muted-foreground">
                        {attachmentRef || 'Attachment'}
                      </Text>
                    </div>
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </DocsAnnotation>
  );
};
